import { useDynamicConfig } from "@dgs/core";
import { isBefore, isSameDay, parseISO } from "date-fns";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BaseApplicationFormValues } from "../shared/types/application";

export const REQUIRED_APPLICATION_FORM_FIELDS: (keyof BaseApplicationFormValues)[] = [
	"setupDate",
	"deletionDate",
	"subdomain",
	"projectNumber",
];

//63 is the maximum allowed common name in SSL certs
const maximumDomainNameLength = 63;

// "analytics." is our longest prefix which is 10 chars long
const longestSubdomainPrefixLength = 10;

export const getMaximumSubdomainLength = (baseDomain: string) => {
	return maximumDomainNameLength - longestSubdomainPrefixLength - baseDomain.length - 1;
};

export const useBaseApplicationFormValidate = () => {
	const { t } = useTranslation();
	const { baseDomain } = useDynamicConfig();
	const maxSubdomainLength = getMaximumSubdomainLength(baseDomain);

	return useCallback(
		(values: BaseApplicationFormValues) => {
			const errors: Record<string, any> = {};

			REQUIRED_APPLICATION_FORM_FIELDS.forEach((requiredField) => {
				if (values[requiredField] === "" || values[requiredField] === null) {
					errors[requiredField] = t("Required field");
				}
			});

			if (values.setupDate && values.deletionDate) {
				const start = parseISO(values.setupDate);
				const end = parseISO(values.deletionDate);
				if (!isSameDay(start, end) && !isBefore(start, end)) {
					errors.setupDate = t("Application setup date must be before or equal to deletion date");
				}
			}

			if (values.subdomain) {
				if (values.subdomain.length > maxSubdomainLength) {
					errors.subdomain = t("The subdomain can not be longer than {{maxLength}} characters.", {
						maxLength: maxSubdomainLength,
					});
				} else if (!/^[a-z\-0-9]+$/.test(values.subdomain)) {
					errors.subdomain = t('The subdomain can only contain the characters a-z, 0-9 and "-".');
				}
			}

			return errors;
		},
		[t, maxSubdomainLength],
	);
};
